/**
 * CompanyModulesTab — platform admin UI for:
 *   - Toggling licensed modules (on / off)
 *   - Setting the agreed per-customer price for each module
 *   - Starting / converting / ending per-module trials (HubSpot-style)
 *   - Applying a platform discount code to the customer
 *
 * Trials are stored as { moduleCode -> ISO expiry } on Companies.ModuleTrials.
 * A module is effectively-enabled if persisted-enabled OR trial active.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import {
  CheckCircle, Circle, Save, RefreshCw, Lock, Sparkles,
  RotateCcw, Clock, Zap, Tag, X
} from 'lucide-react';
import { formatPrice } from '../modules/catalog';

const TRIAL_LENGTH_OPTIONS = [7, 14, 30];
const DEFAULT_TRIAL_DAYS = 14;

export default function CompanyModulesTab({ targetCompanyId, companyName }) {
  const [catalog, setCatalog] = useState([]);
  const [persistedEnabled, setPersistedEnabled] = useState(new Set());
  const [enabled, setEnabled] = useState(new Set());
  const [originalEnabled, setOriginalEnabled] = useState(new Set());

  const [prices, setPrices] = useState({});
  const [originalPrices, setOriginalPrices] = useState({});
  const [overrideCodes, setOverrideCodes] = useState(new Set());

  const [trials, setTrials] = useState({});
  const [pendingTrialActions, setPendingTrialActions] = useState({});

  const [appliedDiscountCode, setAppliedDiscountCode] = useState('');
  const [originalDiscountCode, setOriginalDiscountCode] = useState('');
  const [availableDiscounts, setAvailableDiscounts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const modRes = await api.get(`/companies/${targetCompanyId}/modules`);

      const cat = modRes.data.catalog || [];
      const persisted = new Set(modRes.data.persistedEnabled || modRes.data.enabled || []);
      const effectivePrices = modRes.data.prices || {};
      const overrides = modRes.data.priceOverrides || {};
      const priceStrings = {};
      cat.forEach(m => {
        const v = effectivePrices[m.code];
        priceStrings[m.code] = v == null ? '' : String(v);
      });

      setCatalog(cat);
      setPersistedEnabled(persisted);
      setEnabled(new Set(persisted));
      setOriginalEnabled(new Set(persisted));
      setPrices(priceStrings);
      setOriginalPrices({ ...priceStrings });
      setOverrideCodes(new Set(Object.keys(overrides)));
      setTrials(modRes.data.trials || {});
      setPendingTrialActions({});
      setAppliedDiscountCode(modRes.data.appliedDiscountCode || '');
      setOriginalDiscountCode(modRes.data.appliedDiscountCode || '');

      // Discounts come back inside the modules response (already filtered to active)
      setAvailableDiscounts(modRes.data.availableDiscounts || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }, [targetCompanyId]);

  useEffect(() => { load(); }, [load]);

  const toggle = (code) => {
    setEnabled(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      next.add('core');
      return next;
    });
  };

  const updatePrice = (code, raw) => {
    if (raw === '' || /^\d*(\.\d{0,2})?$/.test(raw)) {
      setPrices(prev => ({ ...prev, [code]: raw }));
    }
  };

  const resetPrice = (code) => {
    const mod = catalog.find(m => m.code === code);
    setPrices(prev => ({ ...prev, [code]: mod?.priceMonthly != null ? String(mod.priceMonthly) : '' }));
  };

  const queueStartTrial = (code, days = DEFAULT_TRIAL_DAYS) => {
    setPendingTrialActions(prev => ({ ...prev, [code]: { action: 'start', days } }));
  };
  const queueEndTrial = (code) => {
    setPendingTrialActions(prev => ({ ...prev, [code]: { action: 'end' } }));
  };
  const queueConvertTrial = (code) => {
    setPendingTrialActions(prev => ({ ...prev, [code]: { action: 'convert' } }));
    setEnabled(prev => new Set([...prev, code, 'core']));
  };
  const cancelPendingTrial = (code) => {
    setPendingTrialActions(prev => {
      const next = { ...prev };
      delete next[code];
      return next;
    });
  };

  const isModulesDirty = (() => {
    if (enabled.size !== originalEnabled.size) return true;
    for (const c of enabled) if (!originalEnabled.has(c)) return true;
    return false;
  })();
  const isPricesDirty = Object.keys(prices).some(c => (prices[c] ?? '') !== (originalPrices[c] ?? ''));
  const isTrialsDirty = Object.keys(pendingTrialActions).length > 0;
  const isDiscountDirty = (appliedDiscountCode || '') !== (originalDiscountCode || '');
  const isDirty = isModulesDirty || isPricesDirty || isTrialsDirty || isDiscountDirty;

  const save = async () => {
    setSaving(true);
    try {
      const codes = [...enabled].filter(c => c !== 'core');
      const priceMap = {};
      for (const [code, str] of Object.entries(prices)) {
        if (str === '' || str == null) continue;
        const n = Number(str);
        if (Number.isFinite(n) && n >= 0) priceMap[code] = n;
      }
      const body = { modules: codes, prices: priceMap };
      if (isTrialsDirty) body.trials = pendingTrialActions;
      if (isDiscountDirty) body.appliedDiscountCode = appliedDiscountCode || null;

      const res = await api.put(`/companies/${targetCompanyId}/modules`, body);

      const persisted = new Set(res.data.persistedEnabled || res.data.enabled || []);
      const effectivePrices = res.data.prices || {};
      const overrides = res.data.priceOverrides || {};
      const priceStrings = {};
      catalog.forEach(m => {
        const v = effectivePrices[m.code];
        priceStrings[m.code] = v == null ? '' : String(v);
      });
      setPersistedEnabled(persisted);
      setEnabled(new Set(persisted));
      setOriginalEnabled(new Set(persisted));
      setPrices(priceStrings);
      setOriginalPrices({ ...priceStrings });
      setOverrideCodes(new Set(Object.keys(overrides)));
      setTrials(res.data.trials || {});
      setPendingTrialActions({});
      setAppliedDiscountCode(res.data.appliedDiscountCode || '');
      setOriginalDiscountCode(res.data.appliedDiscountCode || '');
      if (res.data.availableDiscounts) setAvailableDiscounts(res.data.availableDiscounts);
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to save modules');
    } finally {
      setSaving(false);
    }
  };

  const discard = () => {
    setEnabled(new Set(originalEnabled));
    setPrices({ ...originalPrices });
    setPendingTrialActions({});
    setAppliedDiscountCode(originalDiscountCode);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-3 text-gray-500">
        <RefreshCw className="w-5 h-5 animate-spin" />
        Loading modules…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-8 text-red-600">
        <p className="font-semibold mb-2">Failed to load modules</p>
        <p className="text-sm">{error}</p>
        <button onClick={load} className="mt-4 btn btn-secondary">Retry</button>
      </div>
    );
  }

  // Live billing preview — only paid (non-trial) enabled modules count
  const livePaidEnabled = [...enabled].filter(c => {
    const pending = pendingTrialActions[c];
    if (pending?.action === 'start') return false;
    return true;
  });
  const liveGross = livePaidEnabled.reduce((s, c) => {
    const n = Number(prices[c]);
    return s + (Number.isFinite(n) ? n : 0);
  }, 0);
  const previewDiscount = availableDiscounts.find(d => d.code === appliedDiscountCode) || null;
  const previewDiscountAmount = previewDiscount
    ? (previewDiscount.discountType === 'percent'
        ? Math.round(liveGross * (previewDiscount.amount / 100) * 100) / 100
        : Math.min(liveGross, previewDiscount.amount))
    : 0;
  const liveNet = Math.max(0, liveGross - previewDiscountAmount);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-blue-600" />
          Licensed Modules, Trials &amp; Pricing
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Manage what <span className="font-medium">{companyName}</span> can use, the
          agreed monthly price per module, and any active trials. Trial modules unlock
          functionality for the customer but don't appear on the invoice until converted.
        </p>
      </div>

      {isDirty && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-center justify-between">
          <span>⚠ You have unsaved changes</span>
          <div className="flex gap-2">
            <button
              onClick={discard}
              className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            >
              Discard
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-1 disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* Pricing summary banner with discount picker */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-blue-700 font-semibold">
              Monthly invoice {isDirty ? '(unsaved preview)' : ''}
            </div>
            <div className="text-2xl font-bold text-blue-900">
              {formatPrice(liveNet)}
              <span className="text-sm font-normal text-blue-700"> / month</span>
            </div>
            <div className="text-xs text-blue-700 mt-1">
              {liveGross !== liveNet ? (
                <>Gross {formatPrice(liveGross)} − discount {formatPrice(previewDiscountAmount)}</>
              ) : (
                <>{livePaidEnabled.length} paid module{livePaidEnabled.length === 1 ? '' : 's'}</>
              )}
            </div>
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-blue-700 font-semibold mb-1 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Customer discount
              </label>
              <select
                value={appliedDiscountCode}
                onChange={(e) => setAppliedDiscountCode(e.target.value)}
                className="px-3 py-1.5 text-sm border border-blue-300 rounded bg-white text-gray-900 min-w-[220px]"
              >
                <option value="">— No discount —</option>
                {availableDiscounts.map(d => (
                  <option key={d.discountId} value={d.code}>
                    {d.name} ({d.code}) — {d.discountType === 'percent' ? `${d.amount}%` : formatPrice(d.amount)}
                  </option>
                ))}
                {/* Keep currently-applied code visible even if it's inactive/expired or
                    not in the active list, so the select doesn't blank out. */}
                {appliedDiscountCode && !availableDiscounts.some(d => d.code === appliedDiscountCode) && (
                  <option value={appliedDiscountCode}>
                    {appliedDiscountCode} (inactive)
                  </option>
                )}
              </select>
            </div>
            {appliedDiscountCode && (
              <button
                onClick={() => setAppliedDiscountCode('')}
                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                title="Remove discount"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Module cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {catalog.map(mod => {
          const code = mod.code;
          const on = enabled.has(code);
          const isCore = mod.alwaysOn;
          const isComingSoon = mod.comingSoon;
          const rrp = mod.priceMonthly;
          const hasOverride = overrideCodes.has(code);

          const serverTrial = trials[code];
          const pending = pendingTrialActions[code];
          const persistedOn = persistedEnabled.has(code);
          const trialJustStarted = pending?.action === 'start';
          const trialJustEnded = pending?.action === 'end';
          const trialJustConverted = pending?.action === 'convert';
          const hasActiveTrial = serverTrial?.isActive && !trialJustEnded && !trialJustConverted;
          const canStartTrial = !isCore && !isComingSoon && !persistedOn && !hasActiveTrial && !trialJustStarted;

          return (
            <div
              key={code}
              className={`p-4 border-2 rounded-xl transition-all ${
                isCore
                  ? 'bg-gray-50 border-gray-200'
                  : isComingSoon
                    ? 'bg-gray-50 border-gray-200 opacity-70'
                    : hasActiveTrial
                      ? 'bg-purple-50 border-purple-300'
                      : on
                        ? 'bg-blue-50 border-blue-400'
                        : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`flex items-start gap-3 flex-1 ${!isCore && !isComingSoon ? 'cursor-pointer' : ''}`}
                  onClick={() => !isCore && !isComingSoon && toggle(code)}
                >
                  <span className="text-2xl">{mod.icon || '🧩'}</span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-semibold text-gray-900">{mod.name}</h4>
                      {isCore && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-gray-200 text-gray-700 rounded">
                          <Lock className="w-3 h-3" /> Always on
                        </span>
                      )}
                      {isComingSoon && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          Coming soon
                        </span>
                      )}
                      {hasActiveTrial && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          <Clock className="w-3 h-3" /> Trial · {serverTrial.daysRemaining}d left
                        </span>
                      )}
                      {trialJustStarted && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded">
                          Trial pending ({pending.days}d)
                        </span>
                      )}
                      {trialJustConverted && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded">
                          Converting to paid
                        </span>
                      )}
                      {trialJustEnded && serverTrial && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                          Ending trial
                        </span>
                      )}
                      {hasOverride && !isComingSoon && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded">
                          Custom price
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{mod.description}</p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {on
                    ? <CheckCircle className={`w-6 h-6 ${isCore ? 'text-gray-400' : hasActiveTrial ? 'text-purple-600' : 'text-blue-600'}`} />
                    : <Circle className="w-6 h-6 text-gray-300" />}
                </div>
              </div>

              {/* Price input */}
              {!isComingSoon && (
                <div className="mt-3 pt-3 border-t border-gray-200/70 flex items-center gap-2">
                  <label className="text-xs text-gray-600 font-medium whitespace-nowrap">
                    Price (£/month)
                  </label>
                  <div className="relative flex-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">£</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={prices[code] ?? ''}
                      onChange={(e) => updatePrice(code, e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      placeholder={rrp != null ? String(rrp) : '0'}
                      className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                  </div>
                  {rrp != null && (
                    <button
                      type="button"
                      title={`Reset to RRP ${formatPrice(rrp)}`}
                      onClick={(e) => { e.stopPropagation(); resetPrice(code); }}
                      className="p-1.5 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded"
                    >
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                </div>
              )}
              {!isComingSoon && rrp != null && (
                <div className="text-[11px] text-gray-500 mt-1">
                  RRP: {formatPrice(rrp)} / month
                </div>
              )}

              {/* Trial controls */}
              {!isCore && !isComingSoon && (
                <div className="mt-3 pt-3 border-t border-gray-200/70">
                  {hasActiveTrial ? (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-xs text-purple-800">
                        <Clock className="inline w-3 h-3 mr-1" />
                        Trial ends {new Date(serverTrial.expiresAt).toLocaleDateString()}
                      </div>
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); queueConvertTrial(code); }}
                          className="px-2 py-1 text-xs font-medium bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1"
                          title="Convert this trial to a paid subscription"
                        >
                          <Zap className="w-3 h-3" /> Convert
                        </button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); queueEndTrial(code); }}
                          className="px-2 py-1 text-xs font-medium border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50"
                        >
                          End
                        </button>
                      </div>
                    </div>
                  ) : (trialJustEnded || trialJustConverted) ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-gray-600">Pending — saves on next click</div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); cancelPendingTrial(code); }}
                        className="px-2 py-1 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50"
                      >
                        Undo
                      </button>
                    </div>
                  ) : trialJustStarted ? (
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-xs text-purple-800">
                        <Clock className="inline w-3 h-3 mr-1" />
                        Will start a {pending.days}-day trial on save
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); cancelPendingTrial(code); }}
                        className="px-2 py-1 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50"
                      >
                        Undo
                      </button>
                    </div>
                  ) : canStartTrial ? (
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="text-xs text-gray-600">No active trial</div>
                      <div className="flex gap-1 items-center">
                        {TRIAL_LENGTH_OPTIONS.map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={(e) => { e.stopPropagation(); queueStartTrial(code, d); }}
                            className="px-2 py-1 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700"
                            title={`Start a ${d}-day trial`}
                          >
                            Trial {d}d
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
        ℹ Module &amp; trial changes take effect when the user reloads or signs in again.
        Trial modules don't appear on invoices until they're converted to paid.
      </div>
    </div>
  );
}
